import type { ChessEngine } from "../engine/types";
import { analyzeGame, type AnalyzedMove } from "./analyze";
import { CLASS_EN, CLASS_TR } from "./classify";
import type { CoachLang, CoachMoveCtx } from "./coachPrompt";

export interface ReviewMove extends AnalyzedMove {
  commentary: string | null;
}

export interface Review {
  meta: {
    white: string;
    black: string;
    event: string;
    date: string;
    result: string;
    depth: number;
    coach: boolean;
    coachModel: string | null;
  };
  summary: {
    accuracyWhite: number;
    accuracyBlack: number;
    counts: Record<string, number>;
    noteworthyPlies: number[];
  };
  initialFen: string;
  moves: ReviewMove[];
}

/** chess.com mantığı: hatalar her zaman, "en iyi hamle" ise zor konumdaysa yorumlanır. */
export function needsCommentary(m: AnalyzedMove): boolean {
  if (m.classification === "brilliant") return true;
  if (m.classification === "blunder" || m.classification === "mistake" || m.classification === "inaccuracy") {
    return true;
  }
  return m.classification === "best" && m.winProbBefore < 0.4;
}

function fmtEvalWhite(cp: number | null, mate: number | null, lang: CoachLang): string {
  if (mate != null) return lang === "tr" ? `mat ${Math.abs(mate)}` : `mate ${Math.abs(mate)}`;
  const pawns = (cp ?? 0) / 100;
  return (pawns >= 0 ? "+" : "") + pawns.toFixed(1);
}

export function toCoachCtx(m: AnalyzedMove, lang: CoachLang): CoachMoveCtx {
  const labels = lang === "en" ? CLASS_EN : CLASS_TR;
  return {
    move: `${m.moveNumber}${m.color === "w" ? "." : "..."} ${m.san}`,
    color: m.color === "w" ? "white" : "black",
    classLabel: labels[m.classification],
    winChanceBefore: Math.round(m.winProbBefore * 100),
    winChanceAfter: Math.round(m.winProbAfter * 100),
    evalBeforeWhite: fmtEvalWhite(m.evalBeforeCp, m.mateBefore, lang),
    evalAfterWhite: fmtEvalWhite(m.evalAfterCp, m.mateAfter, lang),
    bestMove: m.bestMoveSan,
    bestLine: m.bestLineSan,
    capture: m.capture,
    check: m.check,
    moveClass: m.classification,
    moveNumber: m.moveNumber,
  };
}

/** Coach'a ulaşılamazsa kullanılan kısa şablon yorumlar. */
export function fallbackCommentary(m: AnalyzedMove, lang: CoachLang): string {
  const en = lang === "en";
  switch (m.classification) {
    case "brilliant":
      return en
        ? `${m.san}!! A brilliant find — the engine's top choice, with material offered up.`
        : `${m.san}!! Taş feda eden harika bir buluş — motorun birinci tercihi.`;
    case "blunder":
      return en
        ? `${m.san} throws away the game. The best move was ${m.bestMoveSan}.`
        : `${m.san} hamlesi oyunu tehlikeye attı. En iyi hamle ${m.bestMoveSan} idi.`;
    case "mistake":
      return en
        ? `${m.san} misses a strong opportunity. ${m.bestMoveSan} was much better.`
        : `${m.san} önemli bir fırsatı kaçırıyor. ${m.bestMoveSan} çok daha iyiydi.`;
    case "inaccuracy":
      return en
        ? `${m.san} gives up a small edge. ${m.bestMoveSan} was slightly more precise.`
        : `${m.san} küçük bir üstünlük kaybettiriyor. ${m.bestMoveSan} biraz daha iyiydi.`;
    case "best":
      return en
        ? `${m.san} — the best move in a critical position!`
        : `${m.san} — kritik konumda en iyi hamleyi buldun!`;
    default:
      return en ? `${m.san} is a solid move.` : `${m.san} sağlam bir hamle.`;
  }
}

export interface BuildReviewOptions {
  depth: number;
  lang: CoachLang;
  /** Hamle yorumlayıcı; verilmezse şablon yorumlar kullanılır. */
  commentate?: (ctx: CoachMoveCtx) => Promise<string | null>;
  coachModel?: string | null;
  onProgress?: (stage: "analyze" | "coach", done: number, total: number) => void;
}

export async function buildReview(pgn: string, engine: ChessEngine, opts: BuildReviewOptions): Promise<Review> {
  const analysis = await analyzeGame(pgn, engine, opts.depth, (done, total) =>
    opts.onProgress?.("analyze", done, total),
  );

  const moves: ReviewMove[] = analysis.moves.map((m) => ({ ...m, commentary: null }));
  const targets = moves.filter(needsCommentary);

  for (let i = 0; i < targets.length; i++) {
    const m = targets[i];
    let text: string | null = null;
    if (opts.commentate) {
      text = await opts.commentate(toCoachCtx(m, opts.lang));
    }
    m.commentary = text ?? fallbackCommentary(m, opts.lang);
    opts.onProgress?.("coach", i + 1, targets.length);
  }

  const h = analysis.headers;
  return {
    meta: {
      white: h["White"] ?? "?",
      black: h["Black"] ?? "?",
      event: h["Event"] ?? "?",
      date: h["Date"] ?? "?",
      result: analysis.result,
      depth: opts.depth,
      coach: Boolean(opts.commentate),
      coachModel: opts.commentate ? (opts.coachModel ?? null) : null,
    },
    summary: {
      accuracyWhite: analysis.accuracyWhite,
      accuracyBlack: analysis.accuracyBlack,
      counts: analysis.counts,
      noteworthyPlies: targets.map((m) => m.ply),
    },
    initialFen: analysis.initialFen,
    moves,
  };
}
